/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { getListingVariants } from './getListingVariants';
import { applyCreateDraftResult } from './helpers/applyCreateDraftResult';
import { getListingChannelId } from './helpers/channelHelpers';
import getListingProducts from './helpers/getListingProducts';
import {
  getListingProductIdFromState,
} from './helpers/entityReadHelpers';
import { upsertListingVariant } from './helpers/entityAdapter';
import { getVariantChannelEntry } from './helpers/listingSyncHelpers';
import resolveSalesChannelId from './helpers/resolveSalesChannelId';
import { showDraftForChannelToast } from './helpers/showDraftForChannelToast';
import { stageListingProductsFromCreateDraft } from './stageListingProductsFromCreateDraft';
import {
  buildCreateDraftMediaForChannel,
  buildListingMediaPayload,
  shouldIncludeDraftMedia,
} from './helpers/buildDraftMediaPayload';
import refreshListingAssetsForChannels from './helpers/refreshListingAssetsForChannels';

export const createVariantDraftFromMaster = createAsyncThunk(
  'products/createVariantDraftFromMaster',
  async ({
    productId,
    channelIds,
    masterVariantIds,
    draftMedia = {},
    overrideExisting = false,
    suppressToast = false,
  }, { dispatch, getState, extra: { sdk, Toast } }) => {
    const productsState = getState().products;

    if (!masterVariantIds?.length) {
      throw new Error('Master variant not found');
    }

    const includeDraftMedia = shouldIncludeDraftMedia(draftMedia.publishingScope);
    const includeGallery = includeDraftMedia && !draftMedia.isVariantSync;

    const channelsToCreate = new Map();
    const createDraftResults = [];

    await Promise.all(channelIds.map(async (channelId) => {
      const listingProductId = getListingProductIdFromState(productsState, productId, channelId);

      const draftVariantIds = [];

      masterVariantIds.forEach((masterVariantId) => {
        const variantChannelEntry = getVariantChannelEntry(productsState, masterVariantId, channelId);

        if (!variantChannelEntry || !listingProductId) {
          const channelVariantIds = channelsToCreate.get(channelId) || [];
          if (!channelVariantIds.includes(masterVariantId)) {
            channelVariantIds.push(masterVariantId);
          }
          channelsToCreate.set(channelId, channelVariantIds);
          return;
        }

        draftVariantIds.push(masterVariantId);
      });

      if (!draftVariantIds.length) return;

      const mediaPayload = includeDraftMedia
        ? buildCreateDraftMediaForChannel({
          channelId,
          productMediaByChannel: draftMedia.productMediaByChannel,
          variantMediaByChannel: draftMedia.variantMediaByChannel,
          masterVariantIds: draftVariantIds,
          includeGallery,
        })
        : {};
      const { data } = await sdk.products.createDraft(listingProductId, {
        source: 'master',
        masterVariantIds: draftVariantIds,
        ...mediaPayload,
        ...(overrideExisting && includeDraftMedia && { replaceExistingDraftAssets: true }),
      });

      createDraftResults.push({
        channelId,
        listing: data.listing,
        variants: data.variants || [],
      });
    }));

    let createdVariants = [];

    if (channelsToCreate.size) {
      const { user: { team } } = getState();
      const variantIdsToCreate = [...new Set([...channelsToCreate.values()].flat())];
      const createChannelIds = [...channelsToCreate.keys()];
      const salesChannelIds = createChannelIds.map(
        (channelId) => resolveSalesChannelId(channelId, team?.salesChannels),
      );
      const listingMedia = includeDraftMedia
        ? buildListingMediaPayload({
          channelIds: createChannelIds,
          productMediaByChannel: draftMedia.productMediaByChannel,
          variantMediaByChannel: draftMedia.variantMediaByChannel,
          salesChannels: team?.salesChannels,
          masterVariantIds: variantIdsToCreate,
          includeGallery,
        })
        : [];
      const { data } = await sdk.products.createListings({
        masterProductId: productId,
        salesChannelIds,
        masterVariantIds: variantIdsToCreate,
        ...(listingMedia.length && { listingMedia }),
      });

      createdVariants = data.createdVariants || [];

      const createdListingProducts = getListingProducts({
        state: getState(),
        productId,
        channelIds: createChannelIds,
        createdListings: data.createdListings || [],
      });

      if (createdListingProducts.length) {
        dispatch(stageListingProductsFromCreateDraft({
          productId,
          listingProducts: createdListingProducts.map((listing) => ({
            channelId: getListingChannelId(listing),
            listingProduct: { ...listing },
            variants: [],
            fromCreateDraft: false,
          })),
        }));
      }

      await Promise.all(createdListingProducts.map((listing) => dispatch(getListingVariants({
        masterProductId: productId,
        channelId: getListingChannelId(listing),
        listingProductId: listing._id,
      })).unwrap()));

      if (!suppressToast) {
        showDraftForChannelToast({
          Toast,
          channelIds: createChannelIds,
          salesChannels: team?.salesChannels || [],
        });
      }
    }

    await refreshListingAssetsForChannels({
      dispatch,
      getState,
      productId,
      channelIds,
    });

    return {
      productId,
      createdVariants,
      createDraftResults,
    };
  },
);

export const reducer = (builder) => {
  builder
    .addCase(createVariantDraftFromMaster.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(createVariantDraftFromMaster.fulfilled, (state, { payload }) => {
      const { productId, createdVariants, createDraftResults } = payload;
      state.isBusy = false;

      createDraftResults.forEach(({ channelId, listing, variants }) => {
        applyCreateDraftResult(state, {
          productId,
          channelId,
          listing,
          variants,
        });
      });

      createdVariants.forEach((listingVariant) => {
        upsertListingVariant(state, listingVariant);
      });
    })
    .addCase(createVariantDraftFromMaster.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot create variant draft';
    });
};
